import { useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
	import.meta.url
).toString();

const options = {
	cMapUrl: '/cmaps/',
	standardFontDataUrl: '/standard_fonts/'
};

/**
 * Renders a PDF viewer component.
 * State for page number and number of pages is managed by the parent component.
 * The parent component must pass in a callback function to handleDocumentLoaded and handlePageLoaded.
 * TODO: create a wrapper component that manages state for page number and number of pages.
 * TODO: add toolbar with page number and page navigation that's false by default, current implementation within ZoomablePDF requires no controls or loading overlay exist in this component.
 * TODO: test scroll pagination mode
 */

/**
 * @typedef {Object} PdfViewerProps
 * @property {string} src - The source of the PDF to be displayed.
 * @property {string} [pagination] - The pagination mode to use. Can be 'zoom' or 'scroll'. Defaults to 'zoom'.
 * @property {function} [handleDocumentLoaded] - The callback function to be called when the document is loaded.
 * @property {function} [handlePageLoaded] - The callback function to be called when the page is loaded.
 * @property {string} [loadingMessage] - The message to display while the PDF is loading.
 * @property {number} [pageNumber] - The page number to display.
 * @property {number} [numPages] - The number of pages in the PDF.
 * @property {function} [setShowDocument] - The callback function to be called when the document is loaded.
 * @returns {JSX.Element} The PDF viewer component.
 */

/**
 * Renders a PDF viewer component.
 * State for page number and number of pages is managed by the parent component.
 * @function PdfViewer
 * @param {PdfViewerProps} props - The props for the component.
 * @returns {JSX.Element} The PDF viewer component.
 */

export function PdfViewer({
	src,
	setShowDocument,
	pagination = 'zoom',
	handleDocumentLoaded,
	handlePageLoaded,
	loadingMessage = '',
	pageNumber = 1,
	numPages
}) {
	const [pageHeight, setPageHeight] = useState(0);

	// Add styles to the PDF viewer and parent using provided refs
	const pageRef = useRef(null);
	const docRef = useRef(null);

	// create function that returns a promise that resolves to the password after user enters password
	function onPassword(callback, reason) {
		function callbackProxy(password) {
			// Cancel button handler
			if (password === null) {
				// password will be null if user clicks on cancel, you should unmount the Document and show custom message, eg:  failed to load  PDF and button with try again
				setShowDocument && setShowDocument(false);

				return;
			}

			callback(password);
		}

		switch (reason) {
			case 1: {
				const password = prompt(
					'Enter the password to open this PDF file.'
				);
				callbackProxy(password);
				break;
			}
			case 2: {
				const password = prompt('Invalid password. Please try again.');
				callbackProxy(password);
				break;
			}
			default:
		}
	}

	// pageHeight should equal whatever the current height of the docRef is but we can't set it until the docRef is loaded
	function onDocumentLoadSuccess({ numPages, ...props }) {
		if (numPages !== 'undefined') {
			handleDocumentLoaded && handleDocumentLoaded(numPages);
			setPageHeight(
				docRef.current
					? docRef.current.getBoundingClientRect().height
					: 0
			);
		}
	}

	const onPageLoadSuccess = () => {
		handlePageLoaded && handlePageLoaded();
	};

	return (
		<Document
			style={{ height: '100%' }}
			inputRef={docRef}
			file={src}
			loading={loadingMessage}
			onLoadSuccess={onDocumentLoadSuccess}
			options={options}
			onPassword={onPassword}>
			{pagination === 'zoom' && (
				<Page
					height={pageHeight}
					onLoadSuccess={onPageLoadSuccess}
					loading={loadingMessage}
					inputRef={pageRef}
					pageNumber={pageNumber}
				/>
			)}
			{pagination === 'scroll' &&
				Array.from(new Array(numPages), (el, index) => (
					<Page
						key={`page_${index + 1}`}
						pageNumber={index + 1}
						onLoadSuccess={onPageLoadSuccess}
					/>
				))}
		</Document>
	);
}
